// `<PropertyDetail>` — top-level route at `/app/properties/:id`.
//
// Composition (UI-SPEC § 4.1 wireframe):
//   GlobalNav (sticky)
//   <main id="main">
//     <PropertyHeader>                     ← glass-strong full-width band
//     [grid lg:grid-cols-[3fr_2fr]]
//       <HeroGallery>                      ← left ~60%
//       <div>
//         <RadarChart>                     ← right ~40%
//         <InfoBadge> (flood)
//         <InfoBadge> (internet)
//     <AgentReportTabs>                    ← full-width below
//   <Toast>                                ← bottom-center, optimistic-rollback
//
// Lifecycle (UI-SPEC § 4.5):
//   isLoading → <PropertyDetailSkeleton>
//   error instanceof PropertyNotFoundError → 404 panel + Back to Dashboard
//   isError → "Couldn't load this property." + Retry button
//   isSuccess → full layout
//
// Data flow:
//   useParams<{id}>() → parse to number → useProperty(id)
//   single GET /api/properties/:id round-trip (DETAIL-02)
//   document.title sync via useEffect with cleanup
//   photo favorite POST handled inside <HeroGallery>; this route surfaces
//   errors via the onFavoriteError → toast pipeline (Plan 06-02 contract).
//
// Pitfall references:
//   #4 (glass a11y) — focus-visible bronze rings on every interactive
//   #10 (always-visible breakdown) — radar paints final shape on first paint
//   #14 (StrictMode double-mount) — useProperty inherits 30s staleTime

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { AlertCircle, Waves, Wifi } from 'lucide-react';

import { GlobalNav } from '@/components/dashboard/GlobalNav';
import { GlassPanel } from '@/components/glass/GlassPanel';
import { HeroGallery } from '@/components/property/HeroGallery';
import { PhotoSourceButtons } from '@/components/property/PhotoSourceButtons';
import { PropertyHeader } from '@/components/property/PropertyHeader';
import { RadarChart, type RadarAxisData } from '@/components/property/RadarChart';
import { InfoBadge, type InfoBadgeProps } from '@/components/property/InfoBadge';
import { AgentReportTabs } from '@/components/property/AgentReportTabs';
import { PropertyDetailSkeleton } from '@/components/property/PropertyDetailSkeleton';
import { useProperty } from '@/hooks/useProperty';
import { PropertyNotFoundError } from '@/lib/api';
import { formatRecencyDays } from '@/lib/format';
import type {
  FloodData,
  InternetProvider,
  PhotoDTO,
  PropertyDetailDTO,
  PropertyRankingDTO,
} from '@/types/db';
import { cn } from '@/lib/utils';

const TOAST_DURATION_MS = 4000;

// ============================================================
// Helpers — keep inline (route-specific; no value re-using elsewhere)
// ============================================================

/** Build the 5-axis radar input. Order locked per UI-SPEC § 3.3 / Plan 02. */
function buildRadarAxes(
  ranking: PropertyRankingDTO,
  photos: PhotoDTO[],
): RadarAxisData[] {
  const favoritedCount = photos.filter((p) => p.is_favorite).length;
  const photosScore = Math.min(100, favoritedCount * 10);
  const dogScore = Math.round((ranking.dog_proximity_factor ?? 0) * 100);
  return [
    {
      label: 'Walk',
      score: ranking.walkability_score,
      tooltip: `Walkability: ${ranking.walkability_score == null ? 'unavailable' : `${ranking.walkability_score}/100`}`,
    },
    {
      label: 'Ent',
      score: ranking.entertainment_score,
      tooltip: `Entertainment: ${ranking.entertainment_score == null ? 'unavailable' : `${ranking.entertainment_score}/100`}`,
    },
    {
      label: 'Dog',
      score: dogScore,
      tooltip: `Dog proximity: ${dogScore}/100`,
    },
    {
      label: 'Recent',
      score: ranking.recency_score,
      tooltip: `Recency: ${ranking.recency_score == null ? 'unavailable' : `${ranking.recency_score}/100`}`,
    },
    {
      label: 'Photos',
      score: photosScore,
      tooltip: `Favorited photos: ${favoritedCount} (×10, cap 100)`,
    },
  ];
}

/** InfoBadge config for the FEMA flood pill. Muted when data missing. */
function floodBadgeProps(flood: FloodData | null): InfoBadgeProps {
  if (!flood || !flood.fema_zone) {
    return {
      icon: Waves,
      label: 'Flood zone',
      value: 'Not available',
      tone: 'muted',
    };
  }
  const zone = flood.fema_zone.toUpperCase();
  // FEMA Special Flood Hazard Areas all start with A (1% annual chance) or
  // V (coastal velocity) — those drive the warning tone. Other zones (X,
  // B, C, D, etc.) are "low/moderate" and use the info tone.
  const isHighRisk = zone.startsWith('A') || zone.startsWith('V');
  const value = flood.risk_level || (isHighRisk ? 'High risk' : 'Low risk');
  return {
    icon: Waves,
    label: `FEMA Zone ${zone}`,
    value,
    tone: isHighRisk ? 'warning' : 'info',
  };
}

/** InfoBadge config for the top internet provider. Muted when none. */
function internetBadgeProps(providers: InternetProvider[]): InfoBadgeProps {
  if (!providers || providers.length === 0) {
    return {
      icon: Wifi,
      label: 'Internet',
      value: 'Not available',
      tone: 'muted',
    };
  }
  const fiber = providers.find((p) => p.fiber_available === 1);
  const top =
    fiber ??
    providers.reduce((a, b) =>
      (a.max_speed_mbps ?? 0) > (b.max_speed_mbps ?? 0) ? a : b,
    );
  let speedLabel = '—';
  if (top.max_speed_mbps != null) {
    if (top.max_speed_mbps >= 1000) {
      speedLabel = `${(top.max_speed_mbps / 1000).toFixed(0)} Gbps`;
    } else {
      speedLabel = `${top.max_speed_mbps} Mbps`;
    }
  }
  return {
    icon: Wifi,
    label: top.connection_type || 'Internet',
    value: `${speedLabel} · ${top.provider_name}`,
    tone: 'info',
  };
}

// ============================================================
// Sub-views — local to this route (no spec value in re-use)
// ============================================================

interface PageShellProps {
  children: React.ReactNode;
  toast?: string | null;
}

function PageShell({ children, toast }: PageShellProps) {
  return (
    <>
      <GlobalNav />
      <main
        id="main"
        className="max-w-[1180px] mx-auto px-4 md:px-6 pt-[88px] pb-12 text-foam-50"
      >
        {children}
      </main>
      {toast && (
        <div
          role="alert"
          aria-live="assertive"
          className={cn(
            'fixed bottom-4 left-1/2 -translate-x-1/2 z-50',
            'px-4 py-3 rounded-2xl',
            'bg-coral-500/15 border border-coral-500/40 text-coral-100',
            'backdrop-blur-md shadow-2xl',
          )}
        >
          {toast}
        </div>
      )}
    </>
  );
}

interface NotFoundPanelProps {
  message?: string;
}

function NotFoundPanel({
  message = 'This property may have been deleted or the link is invalid.',
}: NotFoundPanelProps) {
  return (
    <GlassPanel
      variant="strong"
      className="p-12 text-center max-w-md mx-auto mt-12"
    >
      <AlertCircle
        aria-hidden
        className="w-12 h-12 text-coral-500 mx-auto mb-4"
      />
      <h1 className="text-2xl font-semibold mb-2 text-foam-50">
        Property not found
      </h1>
      <p className="text-foam-50/70 mb-6">{message}</p>
      <Link
        to="/"
        className={cn(
          'inline-block px-4 h-10 leading-10 rounded-xl',
          'bg-bronze-500 text-ocean-950 font-semibold',
          'hover:bg-bronze-500/90 transition-colors duration-150',
          'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 focus-visible:outline-dashed',
        )}
      >
        Back to Dashboard
      </Link>
    </GlassPanel>
  );
}

interface ErrorPanelProps {
  onRetry: () => void;
  message?: string;
}

function ErrorPanel({ onRetry, message }: ErrorPanelProps) {
  return (
    <GlassPanel
      variant="strong"
      className="p-12 text-center max-w-md mx-auto mt-12"
    >
      <AlertCircle
        aria-hidden
        className="w-12 h-12 text-coral-500 mx-auto mb-4"
      />
      <h1 className="text-2xl font-semibold mb-2 text-foam-50">
        Couldn&apos;t load this property.
      </h1>
      <p className="text-foam-50/70 mb-6">
        {message ?? 'Try reloading the page.'}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className={cn(
          'inline-flex items-center px-4 h-10 rounded-xl',
          'bg-bronze-500 text-ocean-950 font-semibold',
          'hover:bg-bronze-500/90 transition-colors duration-150',
          'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2 focus-visible:outline-dashed',
        )}
      >
        Retry
      </button>
    </GlassPanel>
  );
}

interface SuccessLayoutProps {
  data: PropertyDetailDTO;
  onFavoriteError: (msg: string) => void;
}

function SuccessLayout({ data, onFavoriteError }: SuccessLayoutProps) {
  const { days } = formatRecencyDays(data.property.created_at);
  const radarAxes = useMemo(
    () => buildRadarAxes(data.ranking, data.photos),
    [data.ranking, data.photos],
  );
  const floodProps = useMemo(() => floodBadgeProps(data.flood), [data.flood]);
  const internetProps = useMemo(
    () => internetBadgeProps(data.internet),
    [data.internet],
  );

  return (
    <>
      <PropertyHeader
        property={data.property}
        ranking={data.ranking}
        neighborsInRadius={data.neighbors_in_radius}
        listingDetails={data.listing_details}
        daysSinceListed={days}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6 mt-6">
        <div>
          <HeroGallery
            photos={data.photos}
            propertyId={data.property.id}
            addressLabel={data.property.address}
            onFavoriteError={onFavoriteError}
          />
          <PhotoSourceButtons
            propertyId={data.property.id}
            onError={onFavoriteError}
          />
        </div>
        <div className="flex flex-col gap-4 items-center lg:items-start">
          {/* Responsive radar size: 200 (<md) → 240 (md) → 280 (lg+).
              Caller passes the largest dimension; the SVG viewBox keeps
              the chart proportionally readable at any breakpoint. */}
          <RadarChart axes={radarAxes} size={280} className="max-w-full h-auto" />
          <div className="flex flex-wrap gap-2 justify-center lg:justify-start w-full">
            <InfoBadge {...floodProps} />
            <InfoBadge {...internetProps} />
          </div>
        </div>
      </div>

      <div className="mt-8">
        <AgentReportTabs agentReports={data.agent_reports} />
      </div>
    </>
  );
}

// ============================================================
// Top-level route component
// ============================================================

export function PropertyDetail() {
  const { id: idParam } = useParams<{ id: string }>();
  // Parse defensively — useParams returns string|undefined; bad ids land
  // on the 404 panel via the PropertyNotFoundError branch.
  const parsedId = idParam == null ? NaN : Number.parseInt(idParam, 10);
  const id = Number.isFinite(parsedId) && parsedId > 0 ? parsedId : undefined;

  const query = useProperty(id);

  // ---- document.title sync ------------------------------------------
  // Snapshot the previous title once on first mount so cleanup restores
  // exactly what the browser had before navigating in.
  useEffect(() => {
    const address = query.data?.property.address;
    if (!address) return;
    const previous = document.title;
    document.title = `${address} — House Hunter`;
    return () => {
      document.title = previous;
    };
  }, [query.data?.property.address]);

  // ---- Toast state for favorite-error surfacing ---------------------
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), TOAST_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [toast]);

  // ---- Lifecycle dispatch -------------------------------------------
  // 1. Bad id (undefined/NaN) — render 404 panel immediately. The hook is
  //    `enabled: false` in this branch so it never fires a request.
  if (id === undefined) {
    return (
      <PageShell toast={toast}>
        <NotFoundPanel />
      </PageShell>
    );
  }

  if (query.isLoading) {
    return (
      <PageShell toast={toast}>
        <PropertyDetailSkeleton />
      </PageShell>
    );
  }

  if (query.isError) {
    if (query.error instanceof PropertyNotFoundError) {
      return (
        <PageShell toast={toast}>
          <NotFoundPanel />
        </PageShell>
      );
    }
    return (
      <PageShell toast={toast}>
        <ErrorPanel
          onRetry={() => {
            void query.refetch();
          }}
          message={query.error?.message}
        />
      </PageShell>
    );
  }

  if (!query.data) {
    // Defensive fallback — TanStack Query types this as unreachable when
    // !isLoading && !isError, but renderers must be total.
    return (
      <PageShell toast={toast}>
        <PropertyDetailSkeleton />
      </PageShell>
    );
  }

  return (
    <PageShell toast={toast}>
      <SuccessLayout data={query.data} onFavoriteError={setToast} />
    </PageShell>
  );
}

export default PropertyDetail;
